<template>
	<div
		v-if="show"
		:class="slideInClass"
	>
		<nav v-if="!loading && hasMessages">
			<div class="select-container">
				<Select v-model="activeGroup" :options="groupedMessagesOptions"/>
			</div>
			<ul>
				<li
					v-for="group in groupedMessages"
					:key="group.id"
					:class="{
						'active': activeGroup && activeGroup.id === group.id
					}"
				>
					<a href="#" @click.prevent="setActiveGroup(group)">
						<span class="label">{{ getGroupLabel(group) }}</span>
						<span v-if="getGroupSubLabel(group)" class="sublabel">{{ getGroupSubLabel(group) }}</span>
					</a>
				</li>
			</ul>
		</nav>
		<div class="edit-area">
			<header>
				<div class="title-container">
					<div>
						<h1 v-if="activeGroup" v-html="getTitle(activeGroup)"/>
					</div>
					<div class="close-container">
						<a href="#" @click.prevent="closeFan">
							<Icon icon="close"/>
						</a>
					</div>
				</div>
				<div v-if="activeGroup" class="sub-title-container">
					<div class="job-profile-title">{{ latestMessage.jobProfile.info }}</div>
					<div class="job-profile-infos">
						<div class="job-profile-info">
							<span>Ort</span> {{ latestMessage.jobProfile.placesOfWork.join(', ') }}
						</div>
						<div class="job-profile-info" v-b-tooltip :title="industrialSectors">
							<span>Branchen</span> {{ maxLength(industrialSectors, 20) }}
						</div>
					</div>
				</div>
			</header>
			<main ref="main" :class="['scrollbar-check', {'bg-lightest-blue': !loading}]">
				<Loading v-if="loading"/>
				<template v-else>
					<template v-if="hasMessages">
						<div>
							<Loading v-if="loading"/>
							<div v-else-if="initiator" class="message-list">
								<div class="message-list--items">
									<template v-for="message in messages">
										<RequestedVitaInfo
											v-if="isShowRequestedVitaInfo(message)"
											:message="message"
										/>
										<ListItem
											:key="message.id"
											:message="message"
											:is-recipient="isRecipient(message)"
											:is-latest-message="isLatestMessage(message)"
											:initiator="initiator"
											@possibleChange="loadMessages"
											@clickedMoreInfos="clickedMoreInfos"
											@editAccessLink="sendAccessLink = true"
										/>
										<CustomerData
											v-if="isShowCustomerData(message)"
											:customer="message.customer"
										/>
										<ApplicantData
											v-if="isShowApplicantData(message)"
											:disclosed-data="message.disclosedData"
										/>
										<ApplicantAnonymousInfo
											v-if="isShowApplicantAnonymousInfo(message)"
											:customer="message.customer"
										/>
										<SendVita
											v-if="isApplicantMustSendVita(message) && message.vitaInfo"
											v-model="sendVitaData"
											:conversation="message.conversation"
											:vita-info="message.vitaInfo"
											@deleteDocument="confirmVitaDocumentDeletion = true"
										/>
										<ApplicantSentVita
											v-if="isApplicantSentVita(message) && message.vitaInfo"
											:conversation="message.conversation"
											:vita-info="message.vitaInfo"
										/>
										<CustomerPostConnection
											v-if="isShowCustomerPostConnection(message)"
										/>
										<AppointmentResponse
											v-if="isShowAppointmentResponse(message)"
										/>
										<AppointmentSendLink
											v-if="isShowAppointmentSendLink(message)"
											@sendAccessLink="sendAccessLink = true"
										/>
										<PostAppointmentWaiting
											v-if="isShowPostAppointmentWaiting(message)"
										/>
										<CollaborationText
											v-if="isShowCollaborationText(message)"
											:message="message"
										/>
										<CollaborationApplicantDecision
											v-if="isShowCollaborationApplicantDecision(message)"
											:message="message"
										/>
									</template>
								</div>
								<EmploymentInfo
									v-if="latestMessage.employment"
									:message="latestMessage"
									:initiator="initiator"
								/>
							</div>
						</div>
					</template>
					<div v-else>
						Derzeit sind keine Nachrichten vorhanden.
					</div>
				</template>
			</main>
			<footer v-if="!loading && hasMessages">
				<div v-if="isSendOffPossible(latestMessage) && !replying" class="mb-md-2 mb-2 mb-md-0">
					<a href="#" @click.prevent="sendOff">Verabschieden</a>
				</div>
				<div v-if="isShowAppointmentSendLink(latestMessage) || replyable || isApplicantMustSendVita(latestMessage)" class="flex-grow-1 text-right">
					<span v-if="isApplicantMustSendVita(latestMessage)" class="mr-1">
						<Loading v-if="replying" small :fun-fact="false"/>
						<a v-else href="#" @click.prevent="declineVita">Ablehnen und nach Termin fragen</a>
					</span>
					<Button
						v-if="isShowAppointmentSendLink(latestMessage)"
						text="Zugangsdaten schicken"
						icon="checkmark"
						@click="sendAccessLink = true"
					/>
					<Button
						v-if="replyable && !replying"
						:text="replyButtonText"
						:disabled="replyButtonDisabled"
						icon="checkmark"
						@click="reply"
					/>
				</div>
			</footer>
		</div>
		<SendAccessLinkPopUp
			v-if="sendAccessLink && latestMessage.appointment"
			:appointment="latestMessage.appointment"
			@sent="onSentAccessLink"
			@hidden="sendAccessLink = false"
		/>
		<ConfirmPopUp
			v-if="confirmVitaDocumentDeletion"
			title="Lebenslauf löschen"
			:closable="!deletingVitaDocument"
			:confirm-loading="deletingVitaDocument"
			:confirm-disabled="deletingVitaDocument"
			@confirm="deleteUploadedVita"
			@hidden="confirmVitaDocumentDeletion = false"
		>
			Soll dein Lebenslauf wirklich gelöscht werden?
		</ConfirmPopUp>
	</div>
</template>

<script>
import {mapActions, mapState} from "vuex";
import Icon from "@/vue/icon/Icon"
import GetList from "@/api/communication/conversation/message/GetList";
import Loading from "@/vue/common/Loading";
import ListItem from "@/vue/communication/conversation/message/ListItem";
import Button from "@/vue/common/Button";
import {INITIATOR_APPLICANT, INITIATOR_CUSTOMER, OPEN_FAN_KEY} from "@/store/communication/conversation/messages";
import * as ConversationStatus from "@/helper/communication/conversation/Status";
import {get} from "@/helper/communication/conversation/message/ApplicantName";
import {getClasses} from "@/helper/layout/Fan";
import _ from "lodash";
import {scrollToBottom} from "@/helper/Window";
import {getDateTimeComparisonValue} from "@/helper/Date";
import {DateTime} from "luxon";
import {error} from "@/helper/Log";
import Modify from "@/api/communication/conversation/message/Modify";
import {READ} from "@/helper/communication/conversation/message/Status";
import Select from "@/vue/common/form/Select.vue";
import ApplicantAnonymousInfo from "@/vue/communication/conversation/message/reply/ApplicantAnonymousInfo.vue";
import CustomerPostConnection from "@/vue/communication/conversation/message/reply/CustomerPostConnection.vue";
import AppointmentResponse from "@/vue/communication/conversation/message/reply/AppointmentResponse.vue";
import AppointmentSendLink from "@/vue/communication/conversation/message/reply/AppointmentSendLink.vue";
import LabelList from "@/vue/characteristic/LabelList.vue";
import {isEmptyOrNull, maxLength} from "@/helper/String";
import SendAccessLinkPopUp from "@/vue/communication/appointment/SendAccessLinkPopUp.vue";
import PostAppointmentWaiting from "@/vue/communication/conversation/message/reply/PostAppointmentWaiting.vue";
import CollaborationText from "@/vue/communication/conversation/message/reply/CollaborationText.vue";
import CollaborationApplicantDecision
	from "@/vue/communication/conversation/message/reply/CollaborationApplicantDecision.vue";
import EmploymentInfo from "@/vue/communication/conversation/message/EmploymentInfo.vue";
import CustomerData from "@/vue/communication/conversation/message/reply/CustomerData.vue";
import ApplicantData from "@/vue/communication/conversation/message/reply/ApplicantData.vue";
import RequestedVitaInfo from "@/vue/communication/conversation/message/reply/RequestedVitaInfo.vue";
import SendVita from "@/vue/communication/conversation/message/reply/SendVita.vue";
import ApplicantSentVita from "@/vue/communication/conversation/message/reply/ApplicantSentVita.vue";
import {isDefined} from "@/helper/Value";
import Reply, {ACTION_DECLINE_VITA, ACTION_SEND_VITA} from "@/api/communication/conversation/message/Reply";
import * as Toast from "@/helper/layout/Toast";
import flatten from "@/helper/error/Errors";
import ConfirmPopUp from "@/vue/common/popup/ConfirmPopUp.vue";
import Remove from "@/api/application/document/Remove";
import {TYPE_APPLICANT, TYPE_CUSTOMER} from "@/store/job-result/send-off";

export default {

	components: {
		ConfirmPopUp,
		SendVita,
		ApplicantSentVita,
		RequestedVitaInfo,
		ApplicantData,
		CustomerData,
		EmploymentInfo,
		CollaborationApplicantDecision,
		CollaborationText,
		PostAppointmentWaiting,
		SendAccessLinkPopUp,
		LabelList,
		ApplicantAnonymousInfo,
		CustomerPostConnection,
		AppointmentResponse,
		AppointmentSendLink,
		Select,
		Button,
		ListItem,
		Loading,
		Icon,
	},

	data ()
	{
		return {
			loading: true,
			groupedMessages: undefined,
			meta: undefined,
			activeGroup: undefined,
			sendAccessLink: false,
			sendVitaData: undefined,
			replying: false,
			confirmVitaDocumentDeletion: false,
			deletingVitaDocument: false
		};
	},

	computed: {
		...mapState('layout', ['fanLevelClasses']),
		...mapState('communication/conversation/messages', [
			'show',
			'customerData',
			'applicantData',
			'initiator'
		]),
		industrialSectors ()
		{
			return this.latestMessage.jobProfile.industrialSectors.join(', ');
		},
		groupedMessagesOptions ()
		{
			return this.groupedMessages.map(group =>
			{
				return {
					value: group,
					text: this.getGroupLabel(group)
				}
			});
		},
		isCustomerRequestedVita ()
		{
			return this.latestMessage?.messageConversationStatus.id === ConversationStatus.CUSTOMER_REQUESTED_VITA;
		},
		replyButtonDisabled ()
		{
			if (this.isCustomerRequestedVita)
			{
				return !isDefined(this.sendVitaData);
			}

			return false;
		},
		replyButtonText ()
		{
			const messageConversationStatus = this.latestMessage?.messageConversationStatus.id;

			if ([ConversationStatus.CUSTOMER_IS_INTERESTED, ConversationStatus.CUSTOMER_IS_INTERESTED_TOO].includes(messageConversationStatus))
			{
				return 'Meine Daten senden';
			}

			if (messageConversationStatus === ConversationStatus.POST_APPOINTMENT)
			{
				return 'Ergebnis des Termins';
			}

			if (messageConversationStatus === ConversationStatus.APPLICANT_IS_INTERESTED)
			{
				return 'Anfrage annehmen';
			}

			if (messageConversationStatus === ConversationStatus.APPLICANT_SENT_VITA)
			{
				return 'Termin vereinbaren';
			}

			if (messageConversationStatus === ConversationStatus.APPLICANT_DECLINED_VITA)
			{
				return 'Termin/Telefonat vereinbaren';
			}

			if (this.isCustomerRequestedVita)
			{
				return 'Lebenslauf senden';
			}

			return 'Jetzt antworten';
		},
		hasMessages ()
		{
			return this.meta && this.meta.total > 0;
		},
		slideInClass ()
		{
			return getClasses({
				fanLevelClasses: this.fanLevelClasses,
				key: OPEN_FAN_KEY,
				extraClasses: ['communication-conversation-messages-fan', 'slide-in', 'with-nav'],
			});
		},
		messages ()
		{
			if (!this.activeGroup)
			{
				return [];
			}

			const group = _.find(this.groupedMessages, group => group.id === this.activeGroup.id);

			let messages = group?.messages || [];

			return messages.filter(message =>
			{
				const messageConversationStatus = message.messageConversationStatus.id;

				// handled differently
				if (messageConversationStatus === ConversationStatus.COLLABORATION_ACCEPT)
				{
					return false;
				}

				if ([
					ConversationStatus.APPOINTMENT_CONFIRMED,
					ConversationStatus.POST_APPOINTMENT,
					ConversationStatus.SENT_OFF_BY_SYSTEM,
				].includes(messageConversationStatus))
				{
					if (this.initiator === INITIATOR_CUSTOMER && message.recipientType.applicant)
					{
						return false;
					}

					if (this.initiator === INITIATOR_APPLICANT && message.recipientType.customer)
					{
						return false;
					}
				}

				return true;
			});
		},
		latestMessage ()
		{
			if (!this.messages || this.messages.length === 0)
			{
				return undefined;
			}

			return _.last(this.messages);
		},
		replyable ()
		{
			if (!this.latestMessage)
			{
				return false;
			}

			return this.isReplyable(this.latestMessage);
		},
	},

	methods: {
		...mapActions('communication/conversation/messages', [
			'close'
		]),
		...mapActions('job/matching', {
			showJobMatching: 'show'
		}),
		...mapActions('communication/conversation/messages/reply', {
			openReplyFan: 'show',
		}),
		...mapActions('job/vita', {
			openVita: 'show'
		}),
		...mapActions('jobResult/sendOff', {
			openSendOffFan: 'show'
		}),
		maxLength (text, length)
		{
			return maxLength(text, length);
		},
		closeFan ()
		{
			this.sendAccessLink = false;
			this.close();
		},
		getTitle (group)
		{
			const lastMessage = _.last(group.messages);

			if (this.initiator === INITIATOR_APPLICANT)
			{
				if (lastMessage.customer)
				{
					return lastMessage.customer.nameWithSalutation + '<span class="sub">' + lastMessage.customer.companyName + '</span>';
				}

				return lastMessage.jobProfile.info;
			}

			let title = get(lastMessage);

			if (lastMessage.disclosedData)
			{
				title += '<span class="sub">geb. ' + lastMessage.disclosedData.yearOfBirth + '</span>';
			}

			return title;
		},
		setActiveGroup (group)
		{
			this.activeGroup = group;

			this.$nextTick(() => this.scrollToLastMessage());
		},
		getGroupLabel (group)
		{
			const lastMessage = _.last(group.messages);

			if (this.initiator === INITIATOR_APPLICANT)
			{
				return lastMessage.jobProfile.info;
			}

			return get(lastMessage);
		},
		getGroupSubLabel (group)
		{
			const lastMessage = _.last(group.messages);

			if (
				this.initiator === INITIATOR_APPLICANT
				&& lastMessage.customer
			)
			{
				return lastMessage.customer.companyName;
			}

			if (this.initiator === INITIATOR_CUSTOMER)
			{
				return lastMessage.jobProfile.info;
			}

			return undefined;
		},
		isRecipient (message)
		{
			if (this.initiator === INITIATOR_CUSTOMER)
			{
				return message.recipientType.customer;
			}

			return message.recipientType.applicant;
		},
		isInitiator (message)
		{
			if (this.initiator === INITIATOR_CUSTOMER)
			{
				return !message.recipientType.customer;
			}

			return !message.recipientType.applicant;
		},
		isReplyable (message)
		{
			if (this.isInitiator(message))
			{
				return false;
			}

			if (
				this.isRecipient(message)
				&& message.status.waiting
			)
			{
				return false;
			}

			if (
				(
					!this.isRecipient(message)
					&& message.conversationStatus.id === ConversationStatus.COLLABORATION_ACCEPT
				)
				|| !message.status.new
			)
			{
				return false;
			}

			return !_.includes(
				[
					ConversationStatus.SENT_OFF_BY_APPLICANT,
					ConversationStatus.SENT_OFF_BY_CUSTOMER,
					ConversationStatus.SENT_OFF_BY_SYSTEM,
					ConversationStatus.APPOINTMENT_CONFIRMED,
				],
				message.conversationStatus.id
			);
		},
		clickedMoreInfos ()
		{
			if (this.initiator === INITIATOR_CUSTOMER)
			{
				this.openVita({
					jobProfileListItem: this.customerData.jobProfileListItem,
					jobId: this.latestMessage.job.id,
					pseudonym: this.latestMessage.pseudonym,
					disclosedData: this.latestMessage.disclosedData,
					withActions: false,
				});

				return;
			}

			this.openJobMatching();
		},
		isSendOffPossible (message)
		{
			return ![
				ConversationStatus.SENT_OFF_BY_APPLICANT,
				ConversationStatus.SENT_OFF_BY_CUSTOMER,
				ConversationStatus.SENT_OFF_BY_SYSTEM,
				ConversationStatus.POST_APPOINTMENT,
			].includes(message.messageConversationStatus.id);
		},
		isShowRequestedVitaInfo (message)
		{
			return this.isRecipient(message)
				&& [
					ConversationStatus.CUSTOMER_REQUESTED_VITA,
				].includes(message.messageConversationStatus.id);
		},
		isApplicantMustSendVita (message)
		{
			return this.isLatestMessage(message)
				&& this.isShowRequestedVitaInfo(message);
		},
		isApplicantSentVita (message)
		{
			return this.isRecipient(message)
				&& [
					ConversationStatus.APPLICANT_SENT_VITA
				].includes(message.messageConversationStatus.id);
		},
		isShowCustomerData (message)
		{
			return message.customer
				&& this.isRecipient(message)
				&& [
					ConversationStatus.CUSTOMER_IS_INTERESTED,
					ConversationStatus.CUSTOMER_IS_INTERESTED_TOO,
				].includes(message.messageConversationStatus.id);
		},
		isShowApplicantData (message)
		{
			return message.disclosedData
				&& this.isRecipient(message)
				&& [
					ConversationStatus.APPLICANT_IS_INTERESTED_TOO,
				].includes(message.messageConversationStatus.id);
		},
		isShowApplicantAnonymousInfo (message)
		{
			return this.checkExtraMessage(
				message,
				[ConversationStatus.CUSTOMER_IS_INTERESTED, ConversationStatus.CUSTOMER_IS_INTERESTED_TOO],
				false
			);
		},
		isShowCustomerPostConnection (message)
		{
			return this.checkExtraMessage(
				message,
				[ConversationStatus.APPLICANT_IS_INTERESTED_TOO]
			);
		},
		isShowAppointmentResponse (message)
		{
			return this.checkExtraMessage(
				message,
				[
					ConversationStatus.CUSTOMER_SENT_APPOINTMENT_TO_APPLICANT,
					ConversationStatus.APPLICANT_APPOINTMENT_DATE_COUNTER_PROPOSAL
				]
			);
		},
		isShowAppointmentSendLink (message)
		{
			let extraData = message.extraData;

			if (
				!this.isLatestMessage(message)
				|| !message.recipientType.customer
				|| message.messageConversationStatus.id !== ConversationStatus.APPOINTMENT_CONFIRMED
				|| !extraData
				|| !isEmptyOrNull(message.appointment?.accessLink)
			)
			{
				return false;
			}

			extraData = JSON.parse(extraData);

			return !!extraData.linkEligible;
		},
		isShowPostAppointmentWaiting (message)
		{
			return this.isRecipient(message) && message.status.waiting;
		},
		isShowCollaborationText (message)
		{
			return message.messageConversationStatus.id === ConversationStatus.COLLABORATION
				&& this.isRecipient(message)
				&& message.conversation.collaboration;
		},
		isShowCollaborationApplicantDecision (message)
		{
			return message.messageConversationStatus.id === ConversationStatus.COLLABORATION
				&& this.isRecipient(message);
		},
		isLatestMessage (message)
		{
			return message.id === this.latestMessage.id;
		},
		checkExtraMessage (message, statusList, mustBeLatestMessage)
		{
			mustBeLatestMessage = mustBeLatestMessage ?? true;

			if (!_.includes(statusList, message.messageConversationStatus.id))
			{
				return false;
			}

			if (mustBeLatestMessage && !this.isLatestMessage(message))
			{
				return false;
			}

			return this.isRecipient(message);
		},
		openJobMatching ()
		{
			this.showJobMatching({
				job: this.applicantData.job,
				jobProfile: this.activeGroup.jobProfile,
				hasOngoingConversation: true,
				withActions: false
			});
		},
		async deleteUploadedVita ()
		{
			this.deletingVitaDocument = true;

			try
			{
				await new Remove()
					.setDocumentId(this.latestMessage.vitaInfo.documentId)
					.call();

				await this.loadMessages();

				this.confirmVitaDocumentDeletion = false;
			}
			catch (e)
			{
				error(e);
				Toast.error(this, flatten(e.errors));
			}

			this.deletingVitaDocument = false;
		},
		async reply ()
		{
			if (this.isCustomerRequestedVita)
			{
				await this.sendingReplyAction({
					action: ACTION_SEND_VITA,
					sendVitaData: this.sendVitaData
				});

				return;
			}

			await this.openReplyFan({
				title: this.replyButtonText,
				message: this.latestMessage,
				onProcessed: () =>
				{
					this.loadMessages();
				}
			});
		},
		async declineVita ()
		{
			await this.sendingReplyAction({
				action: ACTION_DECLINE_VITA
			});
		},
		async sendingReplyAction ({action, sendVitaData})
		{
			this.replying = true;

			try
			{
				await new Reply()
					.setMessageId(this.latestMessage.id)
					.setAction(action)
					.setSendVitaData(sendVitaData)
					.call();

				await this.loadMessages();
			}
			catch (e)
			{
				error(e);
				Toast.error(this, flatten(e.errors));
			}

			this.replying = false;
		},
		async loadMessages ()
		{
			this.loading = true;

			const call = new GetList()
				.setOffset(0)
				.setLimit(1000);

			if (this.jobProfile)
			{
				call.setJobProfile(this.jobProfile.id);
			}

			if (this.job)
			{
				call.setJob(this.job.id);
			}

			const response = await call.call();

			this.groupedMessages = this.groupMessages(response.data);
			this.meta = response.meta;

			this.loading = false;

			this.$nextTick(() =>
			{
				this.scrollToLastMessage();
			});
		},
		groupMessages (messages)
		{
			let grouped = {};

			_.forEach(messages, message =>
			{
				const conversationId = message.conversation.id;

				if (!grouped[conversationId])
				{
					grouped[conversationId] = {
						id: conversationId,
						job: message.job,
						jobProfile: message.jobProfile,
						messages: []
					};
				}

				grouped[conversationId].messages.push(message);
			});

			grouped = _.values(grouped);

			grouped.sort((a, b) =>
			{
				const latestTimeStampA = getDateTimeComparisonValue(
					DateTime.fromISO(
						_.last(a.messages).timeStamp
					)
				);

				const latestTimeStampB = getDateTimeComparisonValue(
					DateTime.fromISO(
						_.last(b.messages).timeStamp
					)
				);

				return latestTimeStampA === latestTimeStampB
					? 0
					: (latestTimeStampA > latestTimeStampB ? -1 : 1);
			});

			if (!this.activeGroup)
			{
				if (this.customerData?.applicant)
				{
					this.activeGroup = _.find(
						grouped,
						group => group.job.id === this.customerData.applicant.jobId
					);
				}
				else if (this.applicantData?.jobProfile)
				{
					this.activeGroup = _.find(
						grouped,
						group => group.jobProfile.id === this.applicantData.jobProfile.id
					);
				}

				if (!this.activeGroup)
				{
					this.activeGroup = _.first(grouped);
				}
			}
			else
			{
				// reload
				this.activeGroup = _.find(grouped, group => group.id === this.activeGroup.id);
			}

			return grouped;
		},
		scrollToLastMessage ()
		{
			scrollToBottom(this.$refs.main);
		},
		onSentAccessLink ()
		{
			this.loadMessages();
			this.sendAccessLink = false;
		},
		sendOff ()
		{
			let applicantData = undefined;
			let customerData = undefined;

			if (this.initiator === INITIATOR_APPLICANT)
			{
				applicantData = {
					customer: this.latestMessage.customer,
				};
			}
			else
			{
				customerData = {
					disclosedData: this.latestMessage.disclosedData,
					pseudonym: this.latestMessage.pseudonym,
				};
			}

			this.openSendOffFan({
				type: this.initiator === INITIATOR_APPLICANT
					? TYPE_APPLICANT
					: TYPE_CUSTOMER,
				message: this.latestMessage,
				applicantData,
				customerData,
				onClose: ({sentOff}) =>
				{
					if (sentOff)
					{
						this.loadMessages();
					}
				}
			});
		}
	},

	watch: {
		async activeGroup (activeGroup)
		{
			if (activeGroup)
			{
				const messagesToMarkAsRead = _.filter(activeGroup.messages, message =>
				{
					return !this.isInitiator(message)
						&& !this.isReplyable(message)
						&& message.status.new;
				});

				for (const message of messagesToMarkAsRead)
				{
					try
					{
						await new Modify()
							.setMessageId(message.id)
							.setStatus(READ)
							.call();
					}
					catch (e)
					{
						error(e);
					}
				}
			}
		},
		show (show)
		{
			if (show)
			{
				this.loadMessages();
			}
			else
			{
				this.activeGroup = undefined;
				this.meta = undefined;
				this.groupedMessages = undefined;
				this.loading = true;
			}
		}
	}
}
</script>
